import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Alert, AlertDescription } from '../ui/alert';
import { Trash2, Plus, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DealAliasesPanel() {
  const { getAuthHeaders } = useAuth();
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mcaId, setMcaId] = useState('');
  const [systemId, setSystemId] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchAliases = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/deal-aliases`, { headers: getAuthHeaders() });
      setAliases(data);
    } catch {
      toast.error('Failed to load aliases');
    } finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchAliases(); }, [fetchAliases]);

  const handleAdd = async () => {
    const mca = mcaId.trim();
    const sys = systemId.trim();
    if (!mca || !sys) { toast.error('Both fields are required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/deal-aliases`, { mca_id: mca, system_id: sys }, { headers: getAuthHeaders() });
      setMcaId('');
      setSystemId('');
      toast.success(`Alias added: ${mca} → ${sys}`);
      fetchAliases();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add alias');
    } finally { setSaving(false); }
  };

  const handleDelete = async (alias) => {
    try {
      await axios.delete(`${API}/deal-aliases/${alias.id}`, { headers: getAuthHeaders() });
      toast.success(`Alias removed: ${alias.mca_id}`);
      fetchAliases();
    } catch {
      toast.error('Failed to delete alias');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="deal-aliases-panel">
      <Alert className="bg-slate-50 border-slate-200">
        <AlertDescription className="text-xs text-slate-600">
          When a CSV Deal ID is not found during Universal Import, aliases are checked before marking the row as unknown.
        </AlertDescription>
      </Alert>

      {/* Add form */}
      <div className="flex items-center gap-2" data-testid="alias-add-form">
        <Input
          placeholder="MCA ID (e.g. 1100)"
          value={mcaId}
          onChange={(e) => setMcaId(e.target.value)}
          className="w-32 text-sm"
          data-testid="alias-mca-input"
        />
        <ArrowRight className="h-4 w-4 text-slate-400 shrink-0" />
        <Input
          placeholder="System ID (e.g. R101)"
          value={systemId}
          onChange={(e) => setSystemId(e.target.value)}
          className="w-32 text-sm"
          data-testid="alias-system-input"
        />
        <Button
          size="sm"
          onClick={handleAdd}
          disabled={saving || !mcaId.trim() || !systemId.trim()}
          className="bg-slate-900 hover:bg-slate-800 shrink-0"
          data-testid="alias-add-btn"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          <span className="ml-1">Add</span>
        </Button>
      </div>

      {/* Table */}
      {aliases.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4">No aliases configured.</p>
      ) : (
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs">MCA ID</TableHead>
                <TableHead className="text-xs"></TableHead>
                <TableHead className="text-xs">System Deal ID</TableHead>
                <TableHead className="text-xs w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {aliases.map((a) => (
                <TableRow key={a.id} data-testid={`alias-row-${a.mca_id}`}>
                  <TableCell className="text-sm font-mono font-medium">{a.mca_id}</TableCell>
                  <TableCell><ArrowRight className="h-3.5 w-3.5 text-slate-400" /></TableCell>
                  <TableCell className="text-sm font-mono">{a.system_id}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(a)}
                      className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                      data-testid={`alias-delete-${a.mca_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
